import { createSignal, Show, For, createEffect, onCleanup, type Component, type JSX, splitProps } from "solid-js";
import { StIcon } from "@core/components/icons";
import { TbChevronDown } from "solid-icons/tb";
import { classNames } from "@core/lib/classNames";
import type { NavLink, NavSection, DropdownConfig, SimpleDropdownConfig } from "../navbarConfig";
import styles from "./MobileNavbarMenu.module.css";
import { A } from "@solidjs/router";
import { MobileSignUpButton } from "./MobileSignUpButton";

// Helper function to manage body scroll lock (moved here)
const useBodyScrollLock = (locked: () => boolean) => {
  createEffect(() => {
    if (typeof document === "undefined") return;
    const originalStyle = document.body.style.overflow;

    // Defensive check: ensure locked is a function before calling it
    if (typeof locked === "function") {
      if (locked()) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = originalStyle;
      }
    } else {
      // If locked is not a function, log an error and default to original style
      console.error("useBodyScrollLock: 'locked' parameter is not a function. Received:", locked);
      document.body.style.overflow = originalStyle;
    }

    onCleanup(() => {
      document.body.style.overflow = originalStyle;
    });
  });
};

// Helper to create a unique ID for ARIA attributes (moved here)
const createUniqueId = () => `mobile-navbar-dropdown-${Math.random().toString(36).substr(2, 9)}`;

export interface MobileNavbarMenuProps {
  platformConfig: DropdownConfig;
  resourcesConfig: SimpleDropdownConfig;
  useCasesConfig: NavSection[];
  staticLinks: NavLink[];
  isOpen: () => boolean;
  closeMenu: () => void;
  topOffset?: () => string;
}

export const MobileNavbarMenu: Component<MobileNavbarMenuProps> = (props) => {
  console.log("MobileNavbarMenu props.isOpen:", props.isOpen, "typeof props.isOpen:", typeof props.isOpen);

  const [localProps, otherProps] = splitProps(props, [
    "isOpen",
    "closeMenu",
    "platformConfig",
    "resourcesConfig",
    "useCasesConfig",
    "staticLinks",
    "topOffset",
  ]);

  const [mobileAccordionsOpen, setMobileAccordionsOpen] = createSignal<Record<string, boolean>>({});

  useBodyScrollLock(localProps.isOpen);

  const mobilePlatformAccordionId = createUniqueId();
  const mobileUseCasesAccordionId = createUniqueId();
  const mobileResourcesAccordionId = createUniqueId();

  const toggleMobileAccordion = (accordionName: string) => {
    setMobileAccordionsOpen((prev) => ({
      ...prev,
      [accordionName]: !prev[accordionName],
    }));
  };

  const handleLinkClick = () => {
    localProps.closeMenu();
  };

  const getAccordionToggleId = (accordionName: string) => {
    if (accordionName === "platform") return "w-dropdown-toggle-0";
    if (accordionName === "useCases") return "w-dropdown-toggle-1";
    if (accordionName === "resources") return "w-dropdown-toggle-2";
    return "";
  };

  return (
    <nav
      id="mobile-full-menu"
      class={classNames(
        "nav_menu is-page-height-tablet z-index-2 w-nav-menu",
        styles.mobileNavIntendedStyle,
        localProps.isOpen() && styles.mobileNavIntendedStyleOpen,
      )}
      style={localProps.topOffset ? { top: localProps.topOffset() } : {}}
    >
      <div class="nav_center">
        <div class="nav_menu_link-dropdown w-dropdown">
          <div
            role="button"
            class={classNames(
              "nav_menu_link w-dropdown-toggle",
              styles.mobileAccordionHeader,
              mobileAccordionsOpen()["platform"] ? "w--nav-dropdown-toggle-open" : undefined,
            )}
            id={getAccordionToggleId("platform")}
            aria-controls={mobilePlatformAccordionId}
            aria-haspopup="menu"
            aria-expanded={mobileAccordionsOpen()["platform"]}
            tabIndex={0}
            onClick={() => toggleMobileAccordion("platform")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggleMobileAccordion("platform");
            }}
          >
            <div class={classNames("text-gradient", styles.mobileAccordionHeaderText)}>Platform</div>
            <div class={classNames("dropdown-chevron w-embed", styles.mobileAccordionHeaderChevron)}>
              <StIcon icon={TbChevronDown} size={24} class="text-[var(--text-color--text-alternate)]" />
            </div>
            <div class="nav_menu_link-underline" style={!mobileAccordionsOpen()["platform"] ? "opacity: 0;" : ""} />
          </div>
          <Show when={mobileAccordionsOpen()["platform"]}>
            <nav
              class={classNames(
                styles.mobileDropdownPanel,
                mobileAccordionsOpen()["platform"] ? styles.mobileDropdownPanelOpen : undefined,
              )}
              id={mobilePlatformAccordionId}
              aria-labelledby={getAccordionToggleId("platform")}
            >
              <div class={styles.mobileDropdownContent}>
                <For each={localProps.platformConfig.sections}>
                  {(section) => (
                    <div class={styles.mobileDropdownLinkList}>
                      <div class="navbar-dropdown9_dropdown-link-wrap">
                        <h4 class={classNames(styles.mobileDropdownSectionTitle, styles.pinkPurpleGradient)}>
                          {section.title}
                        </h4>
                        {section.subtitle && <h4 class={styles.mobileDropdownSectionSubtitle}>{section.subtitle}</h4>}
                      </div>
                      <div class="spacer-tiny" />
                      <For each={section.links}>
                        {(link) => (
                          <a href={link.href} class={styles.mobileDropdownLink} onClick={handleLinkClick} tabIndex={0}>
                            <div class={styles.mobileDropdownLinkWrap}>
                              <div class={styles.mobileDropdownLinkText}>
                                {link.label}
                                <Show when={link.isComingSoon}>
                                  <span class={styles.spanComingSoon}>Coming Soon</span>
                                </Show>
                              </div>
                              <div class={styles.mobileDropdownLinkDescription}>{link.description || ""}</div>
                            </div>
                          </a>
                        )}
                      </For>
                    </div>
                  )}
                </For>
              </div>
            </nav>
          </Show>
        </div>

        <div class="nav_menu_link-dropdown w-dropdown" style="border-top: 1px solid rgba(255,255,255,0.08);">
          <div
            role="button"
            class={classNames(
              "nav_menu_link w-dropdown-toggle",
              styles.mobileAccordionHeader,
              mobileAccordionsOpen()["useCases"] ? "w--nav-dropdown-toggle-open" : undefined,
            )}
            id={getAccordionToggleId("useCases")}
            aria-controls={mobileUseCasesAccordionId}
            aria-haspopup="menu"
            aria-expanded={mobileAccordionsOpen()["useCases"]}
            tabIndex={0}
            onClick={() => toggleMobileAccordion("useCases")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggleMobileAccordion("useCases");
            }}
          >
            <div class={classNames("text-gradient", styles.mobileAccordionHeaderText)}>Use Cases</div>
            <div class={classNames("dropdown-chevron w-embed", styles.mobileAccordionHeaderChevron)}>
              <StIcon icon={TbChevronDown} size={24} class="text-[var(--text-color--text-alternate)]" />
            </div>
            <div class="nav_menu_link-underline" style={!mobileAccordionsOpen()["useCases"] ? "opacity: 0;" : ""} />
          </div>
          <Show when={mobileAccordionsOpen()["useCases"]}>
            <nav
              class={classNames(
                styles.mobileDropdownPanel,
                mobileAccordionsOpen()["useCases"] ? styles.mobileDropdownPanelOpen : undefined,
              )}
              id={mobileUseCasesAccordionId}
              aria-labelledby={getAccordionToggleId("useCases")}
            >
              <div class={styles.mobileDropdownContent}>
                <For each={localProps.useCasesConfig}>
                  {(section) => (
                    <div class={styles.mobileDropdownLinkList}>
                      <div class="navbar-dropdown9_dropdown-link-wrap">
                        <h4 class={classNames(styles.mobileDropdownSectionTitle, styles.pinkPurpleGradient)}>
                          {section.title}
                        </h4>
                        {section.subtitle && <h4 class={styles.mobileDropdownSectionSubtitle}>{section.subtitle}</h4>}
                      </div>
                      <div class="spacer-tiny" />
                      <For each={section.links}>
                        {(link) => (
                          <a href={link.href} class={styles.mobileDropdownLink} onClick={handleLinkClick} tabIndex={0}>
                            <div class={styles.mobileDropdownLinkText}>
                              {link.label}
                              <Show when={link.isComingSoon}>
                                <span class={styles.spanComingSoon}>Coming Soon</span>
                              </Show>
                            </div>
                          </a>
                        )}
                      </For>
                    </div>
                  )}
                </For>
              </div>
            </nav>
          </Show>
        </div>

        <div class="nav_menu_link-dropdown w-dropdown" style="border-top: 1px solid rgba(255,255,255,0.08);">
          <div
            role="button"
            class={classNames(
              "nav_menu_link w-dropdown-toggle",
              styles.mobileAccordionHeader,
              mobileAccordionsOpen()["resources"] ? "w--nav-dropdown-toggle-open" : undefined,
            )}
            id={getAccordionToggleId("resources")}
            aria-controls={mobileResourcesAccordionId}
            aria-haspopup="menu"
            aria-expanded={mobileAccordionsOpen()["resources"]}
            tabIndex={0}
            onClick={() => toggleMobileAccordion("resources")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") toggleMobileAccordion("resources");
            }}
          >
            <div class={classNames("text-gradient", styles.mobileAccordionHeaderText)}>Resources</div>
            <div class={classNames("dropdown-chevron w-embed", styles.mobileAccordionHeaderChevron)}>
              <StIcon icon={TbChevronDown} size={24} class="text-[var(--text-color--text-alternate)]" />
            </div>
            <div class="nav_menu_link-underline" style={!mobileAccordionsOpen()["resources"] ? "opacity: 0;" : ""} />
          </div>
          <Show when={mobileAccordionsOpen()["resources"]}>
            <nav
              class={classNames(
                styles.mobileDropdownPanel,
                mobileAccordionsOpen()["resources"] ? styles.mobileDropdownPanelOpen : undefined,
              )}
              id={mobileResourcesAccordionId}
              aria-labelledby={getAccordionToggleId("resources")}
            >
              <div class={styles.mobileDropdownContent}>
                <div class={styles.mobileDropdownLinkList}>
                  <For each={localProps.resourcesConfig.links}>
                    {(link) => (
                      <a href={link.href} class={styles.mobileDropdownLink} onClick={handleLinkClick} tabIndex={0}>
                        <div class={styles.mobileDropdownLinkText}>{link.label}</div>
                      </a>
                    )}
                  </For>
                </div>
              </div>
            </nav>
          </Show>
        </div>

        <For each={localProps.staticLinks}>
          {(link) => (
            <a
              href={link.href}
              class={classNames("nav_menu_link w-inline-block", styles.mobileNavLink)}
              onClick={handleLinkClick}
            >
              <div class={styles.mobileNavLinkText}>{link.label}</div>
              <div class={classNames("nav_menu_link-underline", styles.mobileNavLinkUnderline)} />
            </a>
          )}
        </For>
      </div>

      <div class="nav_menu-buttons">
        <A href="/auth/login" class="nav_menu_link underline w-inline-block" onClick={handleLinkClick}>
          <div>Login</div>
        </A>
        <MobileSignUpButton href="/auth/signup" onClick={handleLinkClick}>
          Sign Up
        </MobileSignUpButton>
      </div>
    </nav>
  );
};
